import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const DesignSystem = () => {
  const [activeModal, setActiveModal] = useState(null);
  const [sliderValue1, setSliderValue1] = useState(50);
  const [sliderValue2, setSliderValue2] = useState(50);
  
  // Animation variants for hover effects
  const hoverScale = {
    scale: 1.02,
    transition: { duration: 0.2 }
  };

  return (
    <div className="min-h-screen bg-custom-white p-8">
      <div className="max-w-6xl mx-auto">
        <div className="mb-16">
          <h1 className="text-5xl font-bold text-custom-blue mb-4">Design System Variants</h1>
          <p className="text-xl text-custom-blue/60">Exploring Swiss and Curly design patterns</p>
        </div>

        {/* Text Inputs */}
        <section className="mb-24">
          <h2 className="text-3xl font-bold text-custom-blue mb-8">Text Inputs</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Swiss Input */}
            <div>
              <h3 className="text-xl text-custom-blue/70 mb-6">Swiss Style</h3>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Enter text..."
                  className="w-full px-0 py-6 text-3xl border-0 border-b-[3px] border-custom-blue bg-transparent text-custom-blue placeholder-custom-blue/20 outline-none hover:border-custom-blue focus:border-custom-blue transition-all duration-200 font-light"
                />
                <div className="absolute right-0 top-1/2 -translate-y-1/2">
                  <button className="flex items-center justify-center w-14 h-14 bg-custom-blue text-white hover:bg-custom-blue/90 transition-all duration-200 hover:rotate-90">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Curly Input */}
            <div>
              <h3 className="text-xl text-custom-blue/70 mb-6">Curly Style</h3>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Enter text..."
                  className="w-full px-6 py-4 text-xl border-2 border-custom-blue/20 rounded-2xl bg-white text-custom-blue placeholder-custom-blue/40 outline-none hover:border-custom-blue focus:border-custom-blue transition-all duration-200 shadow-sm hover:shadow-md"
                />
                <button className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center w-12 h-12 bg-custom-blue text-white rounded-xl hover:bg-custom-blue/90 transition-all duration-200 hover:rotate-12">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Buttons */}
        <section className="mb-24">
          <h2 className="text-3xl font-bold text-custom-blue mb-8">Buttons</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Swiss Buttons */}
            <div>
              <h3 className="text-xl text-custom-blue/70 mb-6">Swiss Style</h3>
              <div className="flex gap-4">
                <button className="px-8 py-4 bg-custom-blue text-white hover:bg-custom-blue/90 transition-all duration-200">
                  Primary
                </button>
                <button className="px-8 py-4 border-2 border-custom-blue text-custom-blue hover:bg-custom-blue hover:text-white transition-all duration-200">
                  Secondary
                </button>
              </div>
            </div>

            {/* Curly Buttons */}
            <div>
              <h3 className="text-xl text-custom-blue/70 mb-6">Curly Style</h3>
              <div className="flex gap-4">
                <button className="px-8 py-4 bg-custom-blue text-white rounded-full hover:bg-custom-blue/90 transition-all duration-200 shadow-md hover:shadow-lg hover:scale-105">
                  Primary
                </button>
                <button className="px-8 py-4 border-2 border-custom-blue text-custom-blue rounded-full hover:bg-custom-blue hover:text-white transition-all duration-200 shadow-sm hover:shadow-md hover:scale-105">
                  Secondary
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Cards */}
        <section className="mb-24">
          <h2 className="text-3xl font-bold text-custom-blue mb-8">Cards</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Swiss Card */}
            <motion.div 
              whileHover={hoverScale}
              className="bg-white shadow-lg overflow-hidden cursor-pointer"
            >
              <div className="h-48 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1621622633934-b06225e13f95?q=80&w=2000&auto=format&fit=crop"
                  alt="Card example"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-4">
                <h3 className="text-2xl font-bold text-custom-blue mb-2">
                  Swiss Card Title
                </h3>
                <p className="text-custom-blue/80">
                  Clean and minimalistic design with sharp edges and clear hierarchy.
                </p>
              </div>
            </motion.div>

            {/* Curly Card */}
            <motion.div 
              whileHover={hoverScale}
              className="bg-white shadow-lg overflow-hidden cursor-pointer rounded-3xl"
            >
              <div className="h-48 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1602720160427-84e56bd9ca0d?q=80&w=2000&auto=format&fit=crop"
                  alt="Card example"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-custom-blue mb-2">
                  Curly Card Title
                </h3>
                <p className="text-custom-blue/80">
                  Playful and modern design with rounded corners and soft shadows.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Sliders */}
        <section className="mb-24">
          <h2 className="text-3xl font-bold text-custom-blue mb-8">Sliders</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Swiss Slider */}
            <div>
              <h3 className="text-xl text-custom-blue/70 mb-6">Swiss Style</h3>
              <div className="relative w-full">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={sliderValue1}
                  onChange={(e) => setSliderValue1(e.target.value)}
                  className="w-full h-1 bg-custom-blue/20 rounded-none appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:bg-custom-blue [&::-webkit-slider-thumb]:rounded-none"
                />
                <div className="mt-2 text-custom-blue">Value: {sliderValue1}</div>
              </div>
            </div>

            {/* Curly Slider */}
            <div>
              <h3 className="text-xl text-custom-blue/70 mb-6">Curly Style</h3>
              <div className="relative w-full">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={sliderValue2}
                  onChange={(e) => setSliderValue2(e.target.value)}
                  className="w-full h-2 bg-custom-blue/20 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:bg-custom-blue [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:shadow-md"
                />
                <div className="mt-2 text-custom-blue">Value: {sliderValue2}</div>
              </div>
            </div>
          </div>
        </section>

        {/* Modals */}
        <section className="mb-24">
          <h2 className="text-3xl font-bold text-custom-blue mb-8">Modals</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Swiss Modal Button */}
            <div>
              <h3 className="text-xl text-custom-blue/70 mb-6">Swiss Style</h3>
              <button
                onClick={() => setActiveModal('swiss')}
                className="px-8 py-4 bg-custom-blue text-white hover:bg-custom-blue/90 transition-all duration-200"
              >
                Open Swiss Modal
              </button>
            </div>

            {/* Curly Modal Button */}
            <div>
              <h3 className="text-xl text-custom-blue/70 mb-6">Curly Style</h3>
              <button
                onClick={() => setActiveModal('curly')}
                className="px-8 py-4 bg-custom-blue text-white rounded-full hover:bg-custom-blue/90 transition-all duration-200 shadow-md hover:shadow-lg hover:scale-105"
              >
                Open Curly Modal
              </button>
            </div>
          </div>
        </section>

        {/* Swiss Modal */}
        <AnimatePresence>
          {activeModal === 'swiss' && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ 
                  opacity: 1,
                  transition: { duration: 0.3, ease: "easeOut" }
                }}
                exit={{ 
                  opacity: 0,
                  transition: { duration: 0.3, ease: "easeIn" }
                }}
                className="fixed inset-0 bg-black/45 backdrop-blur-sm z-40"
                onClick={() => setActiveModal(null)}
              />
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ 
                  opacity: 1,
                  y: 0,
                  transition: {
                    duration: 0.3,
                    ease: "easeOut"
                  }
                }}
                exit={{ 
                  opacity: 0,
                  y: 20,
                  transition: { 
                    duration: 0.3,
                    ease: "easeIn"
                  }
                }}
                className="fixed inset-0 z-50 flex items-center justify-center"
              >
                <div className="bg-white w-full max-w-2xl mx-4 shadow-xl">
                  <div className="p-8">
                    <div className="flex justify-between items-center mb-6">
                      <h2 className="text-3xl font-bold text-custom-blue">Swiss Modal</h2>
                      <button
                        onClick={() => setActiveModal(null)}
                        className="p-2 hover:bg-gray-100"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <p className="text-custom-blue/80 mb-6">
                      This modal features clean lines, sharp corners, and minimal decorative elements,
                      following Swiss design principles of clarity and functionality.
                    </p>
                    <div className="flex justify-end gap-4">
                      <button
                        onClick={() => setActiveModal(null)}
                        className="px-6 py-3 border-2 border-custom-blue text-custom-blue hover:bg-custom-blue hover:text-white transition-all duration-200"
                      >
                        Cancel
                      </button>
                      <button className="px-6 py-3 bg-custom-blue text-white hover:bg-custom-blue/90 transition-all duration-200">
                        Confirm
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Curly Modal */}
        <AnimatePresence>
          {activeModal === 'curly' && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ 
                  opacity: 1,
                  transition: { duration: 0.3, ease: "easeOut" }
                }}
                exit={{ 
                  opacity: 0,
                  transition: { duration: 0.3, ease: "easeIn" }
                }}
                className="fixed inset-0 bg-black/45 backdrop-blur-sm z-40"
                onClick={() => setActiveModal(null)}
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ 
                  opacity: 1,
                  scale: 1,
                  transition: {
                    duration: 0.3,
                    ease: "easeOut"
                  }
                }}
                exit={{ 
                  opacity: 0,
                  scale: 0.95,
                  transition: { 
                    duration: 0.3,
                    ease: "easeIn"
                  }
                }}
                className="fixed inset-0 z-50 flex items-center justify-center"
              >
                <div className="bg-white w-full max-w-2xl mx-4 rounded-3xl shadow-xl">
                  <div className="p-8">
                    <div className="flex justify-between items-center mb-6">
                      <h2 className="text-3xl font-bold text-custom-blue">Curly Modal</h2>
                      <button
                        onClick={() => setActiveModal(null)}
                        className="p-2 hover:bg-gray-100 rounded-xl"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <p className="text-custom-blue/80 mb-6">
                      This modal showcases playful rounded corners, soft shadows, and fluid animations,
                      creating a more approachable and modern feel.
                    </p>
                    <div className="flex justify-end gap-4">
                      <button
                        onClick={() => setActiveModal(null)}
                        className="px-6 py-3 border-2 border-custom-blue text-custom-blue rounded-full hover:bg-custom-blue hover:text-white transition-all duration-200 shadow-sm hover:shadow-md hover:scale-105"
                      >
                        Cancel
                      </button>
                      <button className="px-6 py-3 bg-custom-blue text-white rounded-full hover:bg-custom-blue/90 transition-all duration-200 shadow-md hover:shadow-lg hover:scale-105">
                        Confirm
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default DesignSystem;
